import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, TABLES } from '../api/db';
import { Link, useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Loader,
  AlertCircle,
  Crown,
  Users,
  Eye,
  CheckCircle,
  ArrowRight,
  Zap,
  AlertTriangle,
  Target,
} from 'lucide-react';
import { Task, WorkspaceItem } from '../types';
import { TASK_UNIVERSES } from '../constants/taskCreation';

interface RoleGroup {
  role: 'creator' | 'collaborator' | 'reviewer';
  tasks: (Task & { duties?: WorkspaceItem[]; dueUrgency?: 'overdue' | 'soon' | 'normal' })[];
}

const ROLE_CONFIG = {
  creator: { label: 'Created by you', icon: Crown, color: 'text-tech-cyan', bg: 'bg-tech-cyan/10', border: 'border-tech-cyan/25' },
  collaborator: { label: 'Collaborating on', icon: Users, color: 'text-tech-accent', bg: 'bg-tech-accent/10', border: 'border-tech-accent/25' },
  reviewer: { label: 'Reviewing', icon: Eye, color: 'text-purple-400', bg: 'bg-purple-400/10', border: 'border-purple-400/25' },
};

function getDueUrgency(task: Task): 'overdue' | 'soon' | 'normal' {
  if (!task.dueDate) return 'normal';
  const now = Date.now();
  const due = new Date(task.dueDate).getTime();
  if (isNaN(due)) return 'normal';
  if (due < now) return 'overdue';
  if (due - now < 3 * 24 * 60 * 60 * 1000) return 'soon';
  return 'normal';
}

const STATUS_PIPELINE = ['intake', 'in-progress', 'completed'] as const;

const MyWorkspace: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [myTasks, setMyTasks] = useState<Task[]>([]);
  const [connectedTasks, setConnectedTasks] = useState<Task[]>([]);
  const [myDuties, setMyDuties] = useState<WorkspaceItem[]>([]);

  useEffect(() => {
    if (!currentUser) return;

    const load = async () => {
      setLoading(true);
      try {
        const [{ data: hatchRows }, { data: marketRows }] = await Promise.all([
          supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).select('*').eq('user_id', currentUser.uid),
          supabase.from(TABLES.TASKS).select('*').eq('user_id', currentUser.uid),
        ]);
        const map = new Map<string, Task>();
        (hatchRows ?? []).forEach((r) => { const row = r as Record<string, unknown>; map.set(row['id'] as string, row as unknown as Task); });
        (marketRows ?? []).forEach((r) => { const row = r as Record<string, unknown>; map.set(row['id'] as string, row as unknown as Task); });
        setMyTasks(Array.from(map.values()));

        // Load tasks user is connected to
        const [{ data: ownerConns }, { data: taskerConns }] = await Promise.all([
          supabase.from(TABLES.CONNECTION_REQUESTS).select('task_id').eq('task_owner_id', currentUser.uid).eq('status', 'accepted'),
          supabase.from(TABLES.CONNECTION_REQUESTS).select('task_id').eq('tasker_id', currentUser.uid).eq('status', 'accepted'),
        ]);
        const connTaskIds = new Set<string>();
        (taskerConns ?? []).forEach((r) => connTaskIds.add((r as { task_id: string }).task_id));
        (ownerConns ?? []).forEach((r) => connTaskIds.add((r as { task_id: string }).task_id));

        const connTasks: Task[] = [];
        await Promise.all(
          Array.from(connTaskIds).map(async (tid) => {
            if (map.has(tid)) return;
            try {
              const { data: hRow } = await supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).select('*').eq('id', tid).maybeSingle();
              if (hRow) { connTasks.push(hRow as unknown as Task); return; }
              const { data: mRow } = await supabase.from(TABLES.TASKS).select('*').eq('id', tid).maybeSingle();
              if (mRow) connTasks.push(mRow as unknown as Task);
            } catch { /* permission denied for private tasks */ }
          })
        );
        setConnectedTasks(connTasks);

        try {
          const { data: dutiesRows } = await supabase.from(TABLES.WORKSPACE_ITEMS).select('*').eq('assigned_to', currentUser.uid);
          setMyDuties((dutiesRows ?? []) as unknown as WorkspaceItem[]);
        } catch { /* may fail if no workspace items exist */ }
      } catch (err) {
        console.error('MyWorkspace load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  const roleGroups = useMemo((): RoleGroup[] => {
    if (!currentUser) return [];

    const creatorTasks = myTasks.map((t) => ({
      ...t,
      duties: myDuties.filter((d) => d.taskId === t.id),
      dueUrgency: getDueUrgency(t),
    }));

    const collabTasks = connectedTasks
      .filter((t) => t.userId !== currentUser.uid)
      .map((t) => ({
        ...t,
        duties: myDuties.filter((d) => d.taskId === t.id),
        dueUrgency: getDueUrgency(t),
      }));

    return [
      { role: 'creator', tasks: creatorTasks },
      { role: 'collaborator', tasks: collabTasks },
    ].filter((g) => g.tasks.length > 0) as RoleGroup[];
  }, [currentUser, myTasks, connectedTasks, myDuties]);

  const stats = useMemo(() => {
    const allTasks = [...myTasks, ...connectedTasks];
    const active = allTasks.filter((t) => t.status !== 'completed' && t.status !== 'cancelled').length;
    const completed = allTasks.filter((t) => t.status === 'completed').length;
    const overdue = allTasks.filter((t) => getDueUrgency(t) === 'overdue').length;
    const pendingDuties = myDuties.filter((d) => d.status === 'pending' || d.status === 'in-progress').length;
    return { active, completed, overdue, pendingDuties, total: allTasks.length };
  }, [myTasks, connectedTasks, myDuties]);

  if (!currentUser) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-tech-cyan mx-auto mb-3" />
          <p className="text-tech-text-muted">Sign in to see your workspace.</p>
          <Link to="/login" className="text-tech-cyan hover:underline mt-2 block">Login</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader className="w-10 h-10 text-tech-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-full text-tech-text px-4 py-6 md:py-8 bg-tech-mesh">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-tech-cyan/10 border border-tech-cyan/20">
            <Briefcase className="w-6 h-6 text-tech-cyan" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">My Workspace</h1>
            <p className="text-xs sm:text-sm text-tech-text-muted">Everything you own, across all your tasks</p>
          </div>
        </div>

        {/* Stats dashboard */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {([
            { label: 'Active', value: stats.active, icon: Zap, color: 'text-tech-cyan', bg: 'bg-tech-cyan/10' },
            { label: 'Completed', value: stats.completed, icon: CheckCircle, color: 'text-tech-accent', bg: 'bg-tech-accent/10' },
            { label: 'Overdue', value: stats.overdue, icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-400/10' },
            { label: 'My duties', value: stats.pendingDuties, icon: Target, color: 'text-purple-400', bg: 'bg-purple-400/10' },
          ]).map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className={`rounded-xl border border-tech-border/40 ${s.bg} p-4 flex items-center gap-3`}>
                <Icon className={`w-5 h-5 ${s.color} shrink-0`} />
                <div>
                  <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-[10px] text-tech-text-muted uppercase tracking-wider">{s.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pending duties quick-list */}
        {myDuties.filter((d) => d.status !== 'completed').length > 0 && (
          <div className="mb-6 rounded-xl border border-tech-border/40 bg-tech-dark/30 p-4">
            <h2 className="text-sm font-bold text-tech-text mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-purple-400" />
              Your open duties
            </h2>
            <div className="space-y-2">
              {myDuties
                .filter((d) => d.status !== 'completed')
                .slice(0, 8)
                .map((duty) => (
                  <div key={duty.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-tech-gray/20 border border-tech-border/30">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${
                      duty.status === 'in-progress' ? 'bg-tech-cyan' : duty.status === 'blocked' ? 'bg-amber-400' : 'bg-yellow-400'
                    }`} />
                    <span className="text-sm text-tech-text flex-1 truncate">{duty.title}</span>
                    <span className="text-[10px] text-tech-text-muted shrink-0">{duty.status}</span>
                    <Link
                      to={`/tasks/${duty.taskId}`}
                      className="text-[10px] text-tech-cyan hover:underline shrink-0"
                    >
                      Open task
                    </Link>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Role groups */}
        {roleGroups.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-dashed border-tech-border/50">
            <Briefcase className="w-12 h-12 text-tech-text-muted/30 mx-auto mb-3" />
            <p className="text-tech-text-muted mb-1">Your workspace is empty</p>
            <p className="text-xs text-tech-text-muted/60 mb-4">Create a task or connect with others to get started.</p>
            <button
              type="button"
              onClick={() => navigate('/task-hatch')}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-tech-dark"
              style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
            >
              Create your first task
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {roleGroups.map((group) => {
              const config = ROLE_CONFIG[group.role];
              const Icon = config.icon;
              return (
                <div key={group.role}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className={`w-4 h-4 ${config.color}`} />
                    <h2 className="text-sm font-bold text-tech-text uppercase tracking-wider">{config.label}</h2>
                    <span className="text-xs text-tech-text-muted">({group.tasks.length})</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {group.tasks.map((task) => {
                      const universe = TASK_UNIVERSES.find((u) => u.id === task.universe);
                      const dutyCount = task.duties?.length ?? 0;
                      const dutyDone = task.duties?.filter((d) => d.status === 'completed').length ?? 0;

                      return (
                        <Link
                          key={task.id}
                          to={`/tasks/${task.id}`}
                          className={`block rounded-xl border ${config.border} ${config.bg} p-4 hover:bg-white/5 transition-colors group`}
                        >
                          <div className="flex items-start gap-3">
                            {universe && (
                              <span className="text-2xl shrink-0">{universe.emoji}</span>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-tech-text text-sm truncate group-hover:text-tech-cyan transition-colors">
                                {task.title}
                              </p>
                              {task.taskCode && (
                                <p className="font-mono text-[10px] text-tech-text-muted mt-0.5">{task.taskCode}</p>
                              )}
                              <p className="text-xs text-tech-text-muted mt-1 line-clamp-1">{task.goal || task.summary}</p>
                            </div>
                            {task.dueUrgency === 'overdue' && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-500/20 text-red-400 border border-red-500/30 shrink-0">Overdue</span>
                            )}
                            {task.dueUrgency === 'soon' && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30 shrink-0">Due soon</span>
                            )}
                          </div>

                          {/* Mini status pipeline */}
                          <div className="flex items-center gap-0.5 mt-3">
                            {STATUS_PIPELINE.map((stage, idx) => {
                              const order = ['intake', 'analyzing', 'ai-processing', 'neural-requested', 'assigned', 'in-progress', 'completed'];
                              const taskIdx = order.indexOf(task.status);
                              const stageIdx = order.indexOf(stage);
                              const isActive = task.status === stage;
                              const isPast = taskIdx > stageIdx;
                              return (
                                <div key={stage} className="flex items-center flex-1 gap-0.5">
                                  <div className={`h-1 flex-1 rounded-full ${isPast || isActive ? 'bg-tech-cyan' : 'bg-tech-border/40'}`} />
                                  {idx < STATUS_PIPELINE.length - 1 && <div className={`w-1 h-1 rounded-full shrink-0 ${isPast ? 'bg-tech-cyan' : 'bg-tech-border/40'}`} />}
                                </div>
                              );
                            })}
                          </div>

                          {/* Duty summary */}
                          {dutyCount > 0 && (
                            <div className="flex items-center justify-between mt-2 text-[10px] text-tech-text-muted">
                              <span>{dutyDone}/{dutyCount} duties done</span>
                              <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-tech-cyan" />
                            </div>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyWorkspace;
